import cn from 'classnames';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import FormGroup, { FormGroupProps } from '../FormGroup/FormGroup';
import styles from './FormField.scss';

export type FormFieldValue = string | number | undefined;

export type FormFieldProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'id' | 'type' | 'value' | 'onBlur' | 'placeholder'
> &
  FormGroupProps & {
    onChange: (val: FormFieldValue) => void;
    isValidChange?: (newVal: string | undefined) => boolean;
  };

const FormField: FunctionComponent<FormFieldProps> = ({
  id,
  type,
  value,
  onChange,
  onBlur,
  placeholder,
  isValidChange,
  ...props
}) => {
  const isErrored = (props.touched ?? true) && (props.error ?? false);

  const onChangeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue =
        type === 'number' && !isNaN(event.target.valueAsNumber)
          ? event.target.valueAsNumber
          : event.target.value;

      onChange(
        isValidChange
          ? isValidChange(event.target.value)
            ? newValue
            : value?.toString()
          : newValue,
      );
    },
    [type, value, isValidChange, onChange],
  );

  const sanitisedValue = useMemo(
    () => (type === 'number' && isNaN(value as number) ? '' : value),
    [type, value],
  );

  // Block below disables the scroll behaviour which changes the value of an input with type 'number'
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const ref = inputRef.current;
    const handleWheel = (event: MouseEvent) => {
      if (inputRef.current?.id === document.activeElement?.id) {
        event.preventDefault();
      }
    };

    if (ref) {
      ref.addEventListener('wheel', handleWheel);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  return (
    <FormGroup {...props}>
      {props.children}
      <input
        type={type ?? 'text'}
        className={cn(
          'jg-form-control',
          isErrored ? 'jg-form-control--error' : '',
          styles.formField,
        )}
        id={id}
        name={props.name}
        placeholder={placeholder}
        value={sanitisedValue}
        onChange={onChangeHandler}
        onBlur={onBlur}
        ref={type === 'number' ? inputRef : null}
      />
    </FormGroup>
  );
};

export default FormField;
